import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdArrowBackIosNew } from 'react-icons/md';
import NavBar from '../../../components/NavBar';
import API_BASE_URL from '../../../services/config/api';

const RELATORIOS_URL = `${String(API_BASE_URL || '').replace(
	/\/+$/,
	'',
)}/escala/analitico/`;
const MIN_IFRAME_HEIGHT = 480;
const LOAD_TIMEOUT_MS = 20000;

function RelatoriosPage() {
	const iframeRef = useRef(null);
	const iframeLoadedRef = useRef(false);
	const [iframeHeight, setIframeHeight] = useState(MIN_IFRAME_HEIGHT);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const solicitarAtualizacaoAltura = useCallback(() => {
		iframeRef.current?.contentWindow?.postMessage(
			{ type: 'escala-analitico:request-resize' },
			new URL(RELATORIOS_URL).origin,
		);
	}, []);

	useEffect(() => {
		const relatoriosOrigin = new URL(RELATORIOS_URL).origin;

		const timeoutId = setTimeout(() => {
			if (!iframeLoadedRef.current) {
				setIsLoading(false);
				setHasError(true);
			}
		}, LOAD_TIMEOUT_MS);

		const handleMessage = (event) => {
			if (event.origin !== relatoriosOrigin) return;
			if (event.source !== iframeRef.current?.contentWindow) return;

			const data = event.data || {};
			const reportedHeight = Number(
				data.height || data.iframeHeight || data.documentHeight || 0,
			);

			if (!Number.isFinite(reportedHeight) || reportedHeight <= 0) return;

			const height = Math.max(
				MIN_IFRAME_HEIGHT,
				Math.ceil(reportedHeight),
			);

			setIframeHeight(height);
			setIsLoading(false);
			setHasError(false);
			clearTimeout(timeoutId);
		};

		window.addEventListener('message', handleMessage);

		// O clique em um iframe de outra origem não é propagado ao documento pai.
		// Quando ele recebe foco, a janela pai perde foco; esse é o gatilho seguro
		// para pedir uma nova altura, sem recarregar ou alterar o estado do relatório.
		const handleWindowBlur = () => {
			window.setTimeout(() => {
				if (document.activeElement === iframeRef.current) {
					solicitarAtualizacaoAltura();
				}
			}, 0);
		};

		window.addEventListener('blur', handleWindowBlur);

		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener('message', handleMessage);
			window.removeEventListener('blur', handleWindowBlur);
		};
	}, [solicitarAtualizacaoAltura]);

	const handleIframeLoad = () => {
		iframeLoadedRef.current = true;
		setIsLoading(false);
		setHasError(false);
		solicitarAtualizacaoAltura();
	};

	return (
		<div className='flex min-h-screen flex-col bg-[#F0F4F8]'>
			<NavBar />

			<div className='mt-[80px] bg-[#F0F4F8] px-6 py-[36px] text-center font-inter text-[26px] font-semibold text-[#13335A] sm:text-[28px]'>
				Relatórios escala
			</div>

			<div className='border-t border-[#E3E3E3] bg-white'>
				<div className='mx-auto w-[90%] max-w-[1136px] py-4'>
					<Link
						to='/escala'
						className='inline-flex items-center gap-1.5 text-[18px] font-medium text-[#17395F] transition-colors duration-300 hover:text-[#60758F]'
						aria-label='Voltar para a Escala'>
						<MdArrowBackIosNew size={15} />
						Voltar
					</Link>
				</div>
			</div>

			<main className='relative flex-1 bg-white'>
				{isLoading && (
					<div className='absolute inset-0 z-20 flex min-h-[480px] items-center justify-center bg-white'>
						<div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-gray-700' />
					</div>
				)}

				{hasError && (
					<div className='flex min-h-[480px] items-center justify-center px-6 text-center'>
						<p className='max-w-md text-[18px] font-medium text-gray-700'>
							O relatório não pôde ser carregado. Tente novamente
							mais tarde.
						</p>
					</div>
				)}

				{!hasError && (
					<iframe
						ref={iframeRef}
						id='escala-relatorios-frame'
						src={RELATORIOS_URL}
						title='Relat\u00f3rios da escala'
						onLoad={handleIframeLoad}
						onFocus={solicitarAtualizacaoAltura}
						style={{
							width: '100%',
							minHeight: `${MIN_IFRAME_HEIGHT}px`,
							height: `${iframeHeight}px`,
							border: 0,
							display: 'block',
						}}
					/>
				)}
			</main>
		</div>
	);
}

export default RelatoriosPage;
